import { logger } from '../utils/log'
import type { RequestOptions } from '../network/RequestManager'
import { requestManager } from '../network/RequestManager'
import type { SyncDataResponse, GetMessageDataResponse, GetMessageDataNotifyResponse } from '../network/Model'
import { Value } from '../network/Model'
import { messageClient } from '../message/WebsocketMessageClient'

export interface OnDataChangeListener<Value> {
  onDataAdded(packageName: string, map: Map<string, Value>): void
}

export interface OnDeviceChangeListener {
  onDataAdded(packageName: string, data: [object]): void
}

class DataSyncer {
  groupId: string | undefined = undefined
  clientId: string | undefined = undefined
  options: RequestOptions | undefined = undefined
  apiAddress: string | undefined = undefined
  packageName = 'notifyWsMessage'
  devicePackageName = 'deviceWsMessage'
  wsInitTime = 0
  // 基座过来的监听
  // basicListener: OnDataChangeListener<Value>[] = []

  dataChangeListenerMap = new Map<string, OnDataChangeListener<Value>[]>()
  deviceChangeListenerMap = new Map<string, OnDeviceChangeListener[]>()

  init(options: RequestOptions) {
    /* 避免外部同时调用多次初始化 */
    const nowTime = new Date().getTime()

    if (nowTime - this.wsInitTime < 5000) {
      return false
    } else {
      this.wsInitTime = nowTime
    }
    this.options = options
    this.apiAddress = options.apiAddress
    requestManager.init(options)
    requestManager.addOnDataArrivedListener({
      onDataArrived: (resp) => {
        logger.log('DataSyncer DataArrived ')
        let res: any = null

        if (resp) res = JSON.parse(resp)

        //resp 转换为value格式
        // const addedMap = new Map<string, Value>(Object.entries(res))
        /* 这里有两种ws消息  一种是通知消息  一种是设备连接ws消息 */
        if (res?.pushType == 'SYNC_DATA') {
          console.log('ws回来的消息', res)
          const bulidData = res.data
          const newValue = new Value(
            bulidData.globalMessageId,
            bulidData.data,
            bulidData.version,
            bulidData.version,
            bulidData.id,
            bulidData.serverTimestamp
          )
          const addedMap = new Map<string, Value>()

          addedMap.set(bulidData.globalMessageId, newValue)
          this.dataChangeListenerMap.get(this.packageName)?.forEach((listener) => {
            listener.onDataAdded(this.packageName, addedMap)
          })
        }

        /* 设备通知 */
        if (res?.pushType == 'DEVICE_CHANNEL_NOTIFY') {
          this.deviceChangeListenerMap.get(this.devicePackageName)?.forEach((listener) => {
            listener.onDataAdded(this.devicePackageName, res.data.deviceInfos)
          })
        }
      },
    })
  }

  // 查询指定类型notifyType消息
  getDataByNotifyType(notifyType: string, callback: SyncCallback | undefined = undefined) {
    const url = this.apiAddress + '/owork-server/web/async-notify/v1/find-by-type'

    requestManager.request<GetMessageDataNotifyResponse>(
      {
        url,
        notifyType,
        topic: 'find-by-type',
      },
      {
        onSuccess: (topic: string, request, response) => {
          callback?.onSuccess(topic, request, response)
        },
        onFailure: (topic: string, request, response) => {
          logger.log('failed')
          callback?.onFailure(topic, request, response)
        },
        onError: (topic: string, request, exception) => {
          logger.log('error')
          callback?.onError(topic, request, JSON.stringify(exception))
        },
      }
    )
  }

  // 查询全局Id最新的一条消息
  getDataByGlobalMessageId(globalMessageId: string, callback: SyncCallback | undefined = undefined) {
    if (!this.options) {
      return
    }
    const url = this.apiAddress + '/owork-server/web/async-notify/v1/find-last-one-by-global-message-id'

    requestManager.request<GetMessageDataResponse>(
      {
        url,
        globalMessageId,
        topic: 'by-global-message-id',
      },
      {
        onSuccess: (topic: string, request, response) => {
          callback?.onSuccess(topic, request, response)
        },
        onFailure: (topic: string, request, response) => {
          logger.log('failed')
          callback?.onFailure(topic, request, response)
        },
        onError: (topic: string, request, exception) => {
          logger.log('error')
          callback?.onError(topic, request, JSON.stringify(exception))
        },
      }
    )
  }

  syncData(data: Map<string, string>, callback: SyncCallback | undefined = undefined) {
    let jsonData: any = {}
    const obj = Object.fromEntries(data)

    for (const p in obj) {
      jsonData = JSON.parse(obj[p])
      console.log(obj[p])
    }
    const url = this.apiAddress + '/owork-server/web/async-notify/v1/add-one' //发送消息接口地址
    const clientTimestamp = new Date().getTime()

    requestManager.request<SyncDataResponse>(
      {
        url,
        globalMessageId: jsonData.id,
        notifyType: jsonData.category,
        clientTimestamp,
        expireDay: jsonData.expireDay || 1,
        data: JSON.stringify(jsonData),
        deviceId: jsonData.deviceId || jsonData.targetDevice,
        // "webClientId": jsonData.webClientId,
        topic: 'add',
      },
      {
        onSuccess: (topic, request, response) => {
          callback?.onSuccess(topic, request, response)
        },
        onFailure: (topic, request, response) => {
          callback?.onFailure(topic, request, response)
        },
        onError: (topic, request, exception) => {
          callback?.onError(topic, request, JSON.stringify(exception))
        },
      }
    )
  }

  removeData(messageIds: string[], callback: SyncCallback | undefined = undefined) {
    const url = this.apiAddress + '/owork-server/web/async-notify/v1/delete-by-global-message-ids' //删除消息通过Id

    // 直接调用云端的删除方法
    requestManager.request<SyncDataResponse>(
      {
        url,
        globalMessageIds: messageIds,
        topic: 'delete-by-id',
      },
      {
        onSuccess: (topic, request, response) => {
          callback?.onSuccess(topic, request, response)
        },
        onFailure: (topic, request, response) => {
          callback?.onFailure(topic, request, response)
        },
        onError: (topic, request, exception) => {
          callback?.onError(topic, request, JSON.stringify(exception))
        },
      }
    )
  }

  /* 基座的监听 */
  addDataChangeListener(packageName: string, listener: OnDataChangeListener<Value>) {
    this.packageName = packageName
    let listenerList = this.dataChangeListenerMap.get(packageName)

    if (listenerList == null) {
      listenerList = []
      this.dataChangeListenerMap.set(packageName, listenerList)
    }

    if (listenerList.indexOf(listener) < 0) {
      listenerList.push(listener)
    }
  }

  removeDataChangeListener(packageName: string, listener: OnDataChangeListener<Value>) {
    const list = this.dataChangeListenerMap.get(packageName)

    list?.splice(list.indexOf(listener), 1)
  }

  removeAllDataChangeListenerForPackage(packageName: string) {
    this.dataChangeListenerMap.delete(packageName)
  }

  removeAllDataChangeListener() {
    // todo 这里基座有使用
    this.dataChangeListenerMap.clear()
  }

  /* 基座的设备消息监听 */
  addDeviceChangeListener(devicePackageName: string, listener: OnDeviceChangeListener) {
    this.devicePackageName = devicePackageName
    let listenerList = this.deviceChangeListenerMap.get(devicePackageName)

    if (listenerList == null) {
      listenerList = []
      this.deviceChangeListenerMap.set(devicePackageName, listenerList)
    }

    if (listenerList.indexOf(listener) < 0) {
      listenerList.push(listener)
    }
  }

  removeDeviceChangeListener(devicePackageName: string, listener: OnDeviceChangeListener) {
    const list = this.deviceChangeListenerMap.get(devicePackageName)

    list?.splice(list.indexOf(listener), 1)
  }

  removeAllDeviceChangeListener() {
    this.deviceChangeListenerMap.clear()
  }

  close() {
    messageClient.close()
  }
}

export interface SyncCallback {
  onSuccess(topic: string, request: any, response: any): void
  onFailure(topic: string, request: any, response: any): void
  onError(topic: string, request: any, exception: any): void
}

export const dataSyncer = new DataSyncer()
